import { css } from "styled-components";

export type TextVariant =
  | "D1"
  | "D2"
  | "D3"
  | "H1"
  | "H2"
  | "H3"
  | "H4"
  | "H5"
  | "B1"
  | "B2"
  | "B3"
  | "B4"
  | "L1"
  | "L2"
  | "L3"
  | "L4"
  | "L5";

export type BodyTextVariant = "B1" | "B2" | "B3" | "B4";

export type TextWeight = "regular" | "medium";

export type TextTransform = "capitalize" | "uppercase" | "lowercase" | "none";

export type TextAlign = "left" | "center" | "right";

export type As =
  | "div"
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "p"
  | "span"
  | "label";

const BREAKPOINTS = {
  small: "576",
  medium: "768",
  large: "992",
  xlarge: "1280",
};

type BreakpointKey = keyof typeof BREAKPOINTS;

const base = {
  width: "78.75rem",
  text: {
    variants: {
      D1: {
        fontFamily:
          "ClashGrotesk-Variable, Satoshi-Variable, Helvetica, Helvetica Neue, Arial, sans-serif",
        weights: {
          medium: "540",
          regular: "480",
        },
        viewport: {
          mobile: {
            fontSize: "2.5rem",
            lineHeight: "2.5rem",
            letterSpacing: "-0.4px",
          },
          tablet: {
            fontSize: "4rem",
            lineHeight: "4rem",
            letterSpacing: "-0.6px",
          },
          desktop: {
            fontSize: "4.5rem",
            lineHeight: "4.5rem",
            letterSpacing: "-0.6px",
          },
        },
      },
      D2: {
        fontFamily:
          "ClashGrotesk-Variable, Satoshi-Variable, Helvetica, Helvetica Neue, Arial, sans-serif",
        weights: {
          medium: "540",
          regular: "480",
        },
        viewport: {
          mobile: {
            fontSize: "2rem",
            lineHeight: "2rem",
            letterSpacing: "-0.2px",
          },
          tablet: {
            fontSize: "2.5rem",
            lineHeight: "2.5rem",
            letterSpacing: "-0.4px",
          },
          desktop: {
            fontSize: "3rem",
            lineHeight: "3rem",
            letterSpacing: "-0.4px",
          },
        },
      },
      D3: {
        fontFamily:
          "ClashGrotesk-Variable, Satoshi-Variable, Helvetica, Helvetica Neue, Arial, sans-serif",
        weights: {
          medium: "540",
          regular: "480",
        },
        viewport: {
          mobile: {
            fontSize: "1.5rem",
            lineHeight: "1.5rem",
            letterSpacing: "0px",
          },
          tablet: {
            fontSize: "2rem",
            lineHeight: "2rem",
            letterSpacing: "-0.2px",
          },
          desktop: {
            fontSize: "2.5rem",
            lineHeight: "2.5rem",
            letterSpacing: "-0.4px",
          },
        },
      },
      H1: {
        fontFamily:
          "Satoshi-Variable, Helvetica, Helvetica Neue, Arial, sans-serif",
        weights: {
          medium: "640",
          regular: "480",
        },
        viewport: {
          mobile: {
            fontSize: "2.5rem",
            lineHeight: "3rem",
            letterSpacing: "-1px",
          },
          tablet: {
            fontSize: "3.5rem",
            lineHeight: "4rem",
            letterSpacing: "-2px",
          },
          desktop: {
            fontSize: "4rem",
            lineHeight: "4.5rem",
            letterSpacing: "-2px",
          },
        },
      },
      H2: {
        fontFamily:
          "Satoshi-Variable, Helvetica, Helvetica Neue, Arial, sans-serif",
        weights: {
          medium: "640",
          regular: "480",
        },
        viewport: {
          mobile: {
            fontSize: "2rem",
            lineHeight: "2.5rem",
            letterSpacing: "-0.4px",
          },
          tablet: {
            fontSize: "2.5rem",
            lineHeight: "3rem",
            letterSpacing: "-1px",
          },
          desktop: {
            fontSize: "3rem",
            lineHeight: "3.5rem",
            letterSpacing: "-1.6px",
          },
        },
      },
      H3: {
        fontFamily:
          "Satoshi-Variable, Helvetica, Helvetica Neue, Arial, sans-serif",
        weights: {
          medium: "640",
          regular: "480",
        },
        viewport: {
          mobile: {
            fontSize: "1.625rem",
            lineHeight: "2rem",
            letterSpacing: "-0.2px",
          },
          tablet: {
            fontSize: "2rem",
            lineHeight: "2.5rem",
            letterSpacing: "-0.4px",
          },
          desktop: {
            fontSize: "2.5rem",
            lineHeight: "3rem",
            letterSpacing: "-1px",
          },
        },
      },
      H4: {
        fontFamily:
          "Satoshi-Variable, Helvetica, Helvetica Neue, Arial, sans-serif",
        weights: {
          medium: "640",
          regular: "480",
        },
        viewport: {
          mobile: {
            fontSize: "1.25rem",
            lineHeight: "1.5rem",
            letterSpacing: "-0.2px",
          },
          tablet: {
            fontSize: "1.5rem",
            lineHeight: "2rem",
            letterSpacing: "-0.2px",
          },
          desktop: {
            fontSize: "2rem",
            lineHeight: "2.5rem",
            letterSpacing: "-0.4px",
          },
        },
      },
      H5: {
        fontFamily:
          "Satoshi-Variable, Helvetica, Helvetica Neue, Arial, sans-serif",
        weights: {
          medium: "640",
          regular: "480",
        },
        viewport: {
          mobile: {
            fontSize: "1.125rem",
            lineHeight: "1.5rem",
            letterSpacing: "-0.2px",
          },
          tablet: {
            fontSize: "1.25rem",
            lineHeight: "1.5rem",
            letterSpacing: "-0.2px",
          },
          desktop: {
            fontSize: "1.5rem",
            lineHeight: "2rem",
            letterSpacing: "-0.2px",
          },
        },
      },
      B1: {
        fontFamily:
          "Satoshi-Variable, Helvetica, Helvetica Neue, Arial, sans-serif",
        weights: {
          medium: "560",
          regular: "480",
        },
        viewport: {
          mobile: {
            fontSize: "1.125rem",
            lineHeight: "1.5rem",
            letterSpacing: "-0.2px",
          },
          tablet: {
            fontSize: "1.375rem",
            lineHeight: "1.75rem",
            letterSpacing: "-0.2px",
          },
          desktop: {
            fontSize: "1.5rem",
            lineHeight: "2rem",
            letterSpacing: "-0.2px",
          },
        },
      },
      B2: {
        fontFamily:
          "Satoshi-Variable, Helvetica, Helvetica Neue, Arial, sans-serif",
        weights: {
          medium: "560",
          regular: "480",
        },
        viewport: {
          mobile: {
            fontSize: "1rem",
            lineHeight: "1.25rem",
            letterSpacing: "0px",
          },
          tablet: {
            fontSize: "1.125rem",
            lineHeight: "1.5rem",
            letterSpacing: "-0.2px",
          },
          desktop: {
            fontSize: "1.125rem",
            lineHeight: "1.5rem",
            letterSpacing: "-0.2px",
          },
        },
      },
      B3: {
        fontFamily:
          "Satoshi-Variable, Helvetica, Helvetica Neue, Arial, sans-serif",
        weights: {
          medium: "560",
          regular: "480",
        },
        viewport: {
          mobile: {
            fontSize: "1rem",
            lineHeight: "1.25rem",
            letterSpacing: "0px",
          },
          tablet: {
            fontSize: "1rem",
            lineHeight: "1.25rem",
            letterSpacing: "0px",
          },
          desktop: {
            fontSize: "1rem",
            lineHeight: "1.25rem",
            letterSpacing: "0px",
          },
        },
      },
      B4: {
        fontFamily:
          "Satoshi-Variable, Helvetica, Helvetica Neue, Arial, sans-serif",
        weights: {
          medium: "560",
          regular: "480",
        },
        viewport: {
          mobile: {
            fontSize: "0.875rem",
            lineHeight: "1.25rem",
            letterSpacing: "0px",
          },
          tablet: {
            fontSize: "0.875rem",
            lineHeight: "1.25rem",
            letterSpacing: "0px",
          },
          desktop: {
            fontSize: "0.875rem",
            lineHeight: "1.25rem",
            letterSpacing: "0px",
          },
        },
      },
      L1: {
        fontFamily:
          "Satoshi-Variable, Helvetica, Helvetica Neue, Arial, sans-serif",
        weights: {
          medium: "640",
        },
        viewport: {
          mobile: {
            fontSize: "1.125rem",
            lineHeight: "1.5rem",
            letterSpacing: "-0.1px",
          },
          tablet: {
            fontSize: "1.125rem",
            lineHeight: "1.75rem",
            letterSpacing: "-0.1px",
          },
          desktop: {
            fontSize: "1.25rem",
            lineHeight: "1.75rem",
            letterSpacing: "-0.1px",
          },
        },
      },
      L2: {
        fontFamily:
          "Satoshi-Variable, Helvetica, Helvetica Neue, Arial, sans-serif",
        weights: {
          medium: "640",
        },
        viewport: {
          mobile: {
            fontSize: "1rem",
            lineHeight: "1.375rem",
            letterSpacing: "-0.1px",
          },
          tablet: {
            fontSize: "1.125rem",
            lineHeight: "1.5rem",
            letterSpacing: "-0.1px",
          },
          desktop: {
            fontSize: "1.125rem",
            lineHeight: "1.5rem",
            letterSpacing: "-0.1px",
          },
        },
      },
      L3: {
        fontFamily:
          "Satoshi-Variable, Helvetica, Helvetica Neue, Arial, sans-serif",
        weights: {
          medium: "640",
        },
        viewport: {
          mobile: {
            fontSize: "0.875rem",
            lineHeight: "1.125rem",
            letterSpacing: "-0.1px",
          },
          tablet: {
            fontSize: "1rem",
            lineHeight: "1.375rem",
            letterSpacing: "-0.1px",
          },
          desktop: {
            fontSize: "1rem",
            lineHeight: "1.375rem",
            letterSpacing: "-0.1px",
          },
        },
      },
      L4: {
        fontFamily:
          "Satoshi-Variable, Helvetica, Helvetica Neue, Arial, sans-serif",
        weights: {
          medium: "640",
        },
        viewport: {
          mobile: {
            fontSize: "0.75rem",
            lineHeight: "1rem",
            letterSpacing: "-0.1px",
          },
          tablet: {
            fontSize: "0.875rem",
            lineHeight: "1.125rem",
            letterSpacing: "-0.1px",
          },
          desktop: {
            fontSize: "0.875rem",
            lineHeight: "1.125rem",
            letterSpacing: "-0.1px",
          },
        },
      },
      L5: {
        fontFamily:
          "Satoshi-Variable, Helvetica, Helvetica Neue, Arial, sans-serif",
        weights: {
          medium: "640",
        },
        viewport: {
          mobile: {
            fontSize: "0.75rem",
            lineHeight: "0.75rem",
            letterSpacing: "+1px",
          },
          tablet: {
            fontSize: "0.75rem",
            lineHeight: "0.75rem",
            letterSpacing: "+1px",
          },
          desktop: {
            fontSize: "0.75rem",
            lineHeight: "0.75rem",
            letterSpacing: "+1px",
          },
        },
      },
    },

    textAligns: {
      left: "left",
      center: "center",
      right: "right",
    },

    textTransforms: {
      capitalize: "capitalize",
      uppercase: "uppercase",
      lowercase: "lowercase",
      none: "none",
    },
  } as const,

  breakpoints: {
    up: (key: BreakpointKey) =>
      `@media (min-width: ${parseInt(BREAKPOINTS[key])}px)`,
    between: (
      firstBreakPoint: BreakpointKey,
      secondBreakPoint: BreakpointKey,
    ) =>
      `@media (min-width: ${parseInt(BREAKPOINTS[firstBreakPoint])}px)
      and (max-width: ${parseInt(BREAKPOINTS[secondBreakPoint]) - 1}px)`,
    down: (key: BreakpointKey) =>
      `@media (max-width: ${parseInt(BREAKPOINTS[key]) - 1}px)`,
  },

  colors: {
    blue500: "#1E90FF",
    blue700: "#0E65B9",
    blue900: "#081b2d",
    purple300: "#A881FE",
    purple500: "#6419FF",
    neutrals: {
      white500: "#FFFFFF",
      black500: "#030303",
      gray50: "#FEFEFE",
      gray100: "#F5F5F5",
      gray200: "#E8E8E8",
      gray300: "#C2C2C2",
      gray500: "#7F7F7F",
      gray700: "#2E2E2E",
      gray800: "#212121",
      gray900: "#111111",
    },
  },
};

const light = {
  ...base,
  mode: "light",
  colors: {
    ...base.colors,
    background: "#ffffff",
    text: "#000000",
    card: "#f0f0f0",
    black: "#000000",

    tokens: {
      text: {
        textActivated: "#111111",
        textDefault: "#2E2E2E",
        textDisabled: "#7F7F7F",
      },
    },
  },
};

const dark = {
  ...base,
  mode: "dark",
  colors: {
    ...base.colors,
    background: "#000",
    text: "#ffffff",
    card: "#1a1a1a",
    black: "#000000",

    tokens: {
      text: {
        textActivated: "#FFFFFF",
        textDefault: "#F5F5F5",
        textDisabled: "#7F7F7F",
      },
    },
  },
};

export const themes = {
  light,
  dark,
};

export type ThemeMode = keyof typeof themes;
export type Theme = (typeof themes)["light"] & (typeof themes)["dark"];
